import type { GQL_ID } from '@@partner:types:graphql'
import type { RsuiteItemDataType } from '@@partner:types:app'

import React, { useCallback, useState, useEffect } from 'react'
import { useCategoriesFetcher } from '@@partner:graphql/query/catalog/categoriesWithParent.gql'
import { convertCategoryToItemDataType } from '@@partner:helpers/data-mappers/category'

import { MultiCascader, Button, Modal, ButtonGroup } from 'rsuite'

export interface ProductCategoriesPickerModalProps {
  value: GQL_ID[]
  open: boolean
  onChange: (selectedIds: GQL_ID[]) => void
  onClose: () => void
}

export default function ProductCategoriesPickerModal ({
  value,
  onChange,
  onClose,
  open = false
}: ProductCategoriesPickerModalProps) {
  const { fetchCategories, fetchRootCategories } = useCategoriesFetcher()
  const [ state, setState ] = useState<GQL_ID[]>(value || [])
  const [ data, setData ] = useState<RsuiteItemDataType[]>([])

  // watch for prop changes
  useEffect(() => {
    setState(value)
  }, [ value ])

  // initial data loading
  useEffect(() => {
    const preload = async () => {
      const initial = await fetchRootCategories({ skipIds: [] })
      setData(initial.categories.edges.map(convertCategoryToItemDataType))
    }

    preload()
  }, [ fetchRootCategories ])

  // reset state and close modal
  const handleCloseModal = useCallback(() => {
    setState(value)
    onClose()
  }, [ value, onClose ])

  // expose new state and close
  const handleSaveChosenCategories = useCallback(() => {
    onChange(state)
    onClose()
  }, [ onChange, state, onClose ])

  // categories selecting
  const handleCategoriesSelect = useCallback((newState: GQL_ID[]) => {
    setState(newState)
  }, [])

  // rsuite specific handler
  // loads children categories for choosen
  const handleGetChildren = useCallback(
    async (node: RsuiteItemDataType) => {
      if (node.value) {
        const data = await fetchCategories({
          parentId: node.value,
          skipIds: []
        })

        return data.categories.edges.map(convertCategoryToItemDataType)
      }

      return []
    },
    [ fetchCategories ]
  )

  return (
    <Modal open={open} onClose={handleCloseModal}>
      <Modal.Header>Добавить привязку к категории</Modal.Header>
      <Modal.Body>
        <MultiCascader
          inline
          menuAutoWidth
          cascade={false}
          searchable={false}
          data={data}
          value={state}
          onCheck={handleCategoriesSelect}
          getChildren={handleGetChildren}
        />

        <ButtonGroup justified>
          <Button onClick={handleCloseModal}>Отмена</Button>
          <Button
            onClick={handleSaveChosenCategories}
            color="green"
            appearance="primary"
          >
            OK
          </Button>
        </ButtonGroup>
      </Modal.Body>
    </Modal>
  )
}
